// @ts-strict-ignore
import { Injectable, Type } from '@angular/core';
import { AppConstants } from '@utils/app.constants';
import { LMContextService } from '@services/index';
import { LMConditionUtilService } from "@app/utils";

// todo pwpatton make into an enumerated string once we get typescript 2.4
// https://github.com/Microsoft/TypeScript/wiki/What's-new-in-TypeScript#string-enums

/**
 * Use this util service to compute things based off of a listSpec.
 */
@Injectable()
export class LMListSpecUtilService {

  constructor(
    private lmContextService: LMContextService,
    private lmConditionUtilService: LMConditionUtilService
  ) {}

  public getSelectionMode(listSpec: LMListSpec, inSelectListEditMode = false, allowMultiSelections = false): SohoDataGridSelectable {
    if (listSpec.listType === 'select') {
      if (this.lmContextService.useSingleSelectList()) {
        if (inSelectListEditMode) {
          return (listSpec.busListSpec.selection === 'multiple') ? 'mixed' : 'single';
        } else {
          return (allowMultiSelections) ? 'mixed' : 'single';
        }
      }
    }

    if (listSpec.drillAroundMode) {
      return 'single';
    }

    if (listSpec.showAsMingleWidget) {
      return 'single';
    }

    if (listSpec.busListSpec.implementsCubeView) {
      return 'siblings';
    }

    if (listSpec.busListSpec.selection === 'disabled') {
      return false; // selection disabled
    }

    if (listSpec.busListSpec.selection === 'multiple') {
      return 'mixed';
    }

    return 'single';
  }

  /**
   * Consistent way to get a lists context menu id to use to pass
   * into the datagrids menuId option.
   */
  public getContextMenuId(listSpec: LMListSpec) {
    return listSpec.busListSpec.id + '_ContextMenu';
  }

  public hasDetailSections(listSpec: LMListSpec): boolean {
    if (listSpec && listSpec.busListSpec) {
      return (listSpec.busListSpec.detailSections !== undefined);
    }
    return false;
  }

  public hasDetailFields(listSpec: LMListSpec): boolean {
    return !!(listSpec?.busListSpec?.detailFields);
  }

  public hasFilterList(detailSection: LMBusDetailSection): boolean {
    return !!detailSection.filterListSpecs;
  }

  public isFilterList(listSpec: LMListSpec): boolean {
    return listSpec.listType === 'filterlist';
  }

  public isCubeView(listSpec: LMListSpec): boolean {
    return listSpec && listSpec.busListSpec.implementsCubeView;
  }

  public isColumnarView(listSpec: LMListSpec): boolean {
    return listSpec && listSpec.busListSpec.implementsColumnarView;
  }

  public isTreeView(listSpec: LMListSpec): boolean {
    return listSpec && listSpec.busListSpec.implementsTreeView;
  }

  public isAudit(listSpec: LMListSpec): boolean {
    return listSpec.busListSpec.isAudit;
  }

  public implementsAuditCompare(listSpec: LMListSpec): boolean {
    return listSpec.busListSpec.implementsAuditCompare;
  }

  public showChangedValuesOnRight(listSpec: LMListSpec): boolean {
    return listSpec.busListSpec.showChangedValuesOnRight;
  }

  // Need listStateInfo to track active list if using chart switcher
  public isFilterable(listSpec: LMListSpec, listStateInfo: LMListState): boolean {
    if (listSpec && listSpec.busListSpec) {
      if (listSpec.busListSpec.implementsRepresentativeImageView || listSpec.busListSpec.implementsOrgChartView || listSpec.busListSpec.implementsGalleryView) {

        // Check to see if active list (chart switcher) is a standard list
        if (listStateInfo?.displayAs === AppConstants.LISTVIEWTYPE_LIST) {
          return true;
        }

        return false;
      }

      const filterableField = listSpec.busListSpec.displayFields.find(displayField => displayField.filterable);
      if (filterableField) {
        return true;
      }
    }

    return false;
  }

  public isCardViewWithDetailSections(listSpec: LMListSpec): boolean {
    if (listSpec && listSpec.busListSpec) {
      const hasDetailSections = this.hasDetailSections(listSpec);
      const isCardView = listSpec.busListSpec.displayFields.map((displayField: LMListDisplayField): boolean => {
        if (displayField.type.toUpperCase() === AppConstants.CARDVIEW) {
          return true;
        }
      })[ 0 ];

      return hasDetailSections && isCardView;
    }

    return false;
  }

  /**
   * Whether this list has only a card view on it and no detail fields.
   * @param listSpec
   * @returns
   */
  public isCardViewOnly(listSpec: LMListSpec): boolean {
    if (listSpec && listSpec.busListSpec) {
      if (listSpec.busListSpec.displayFields.length === 1) {
        return listSpec.busListSpec.displayFields.map((displayField: LMListDisplayField): boolean => {
          if (displayField.type.toUpperCase() === AppConstants.CARDVIEW) {
            return true;
          }
        })[ 0 ];
      }
    }
    return false;
  }

  public isGallery(listSpec: LMListSpec): boolean {
    return listSpec?.busListSpec?.implementsGalleryView && !this.hasDisplayFields(listSpec);
  }

  public allowHorizontalPaging(listSpec: LMListSpec): boolean {
    return listSpec.busListSpec.allowHorizontalPaging && ( this.isMultiPanePanelContent(listSpec) || this.isMultiListPanelContent(listSpec));
  }

  public hasDisplayFields(listSpec): boolean {
    return listSpec.busListSpec.displayFields.some(displayField => !displayField.isHidden);
  }

  public allowUpdate(listSpec: LMListSpec): boolean {
    if (listSpec.drillAroundMode) {
      return false;
    }

    if (this.lmContextService.isArchiveMode()) {
      return false;
    }

    if (listSpec.busListSpec.userFolderArchiveReport !== undefined || listSpec.busListSpec.userFolderAsOfDate !== undefined) {
      return false;
    }

    if (this.isColumnarView(listSpec)) {
      return false;
    }

    if (this.hasDetailSections(listSpec)) {

      // --------------------------------------------
      // are detail sections updatable.
      // --------------------------------------------
      // const detailSectionAllowsUpdate = listSpec.busListSpec.detailSections.find(
      //   (detailSection: LMBusDetailSection) => !!detailSection.listSpec && this.allowUpdate(detailSection.listSpec));

      const detailSectionAllowsUpdate = listSpec.busListSpec.detailSections.find(
        (detailSection: LMBusDetailSection) => {
          switch (detailSection.type) {
            /** a detail section that is a detail section?!?! */
            case 'detailsection':
            case 'cardview':
            case 'instancecountchart':
            case 'link':
            case 'standardform':
            case 'dashboard':
              return false; // none of these detail sections are editable.

            case 'list':
              return this.allowUpdate(detailSection.listSpec);
          }
          return false;
        });

      if (detailSectionAllowsUpdate) {
        return true;
      }
    }

    if (this.isCardViewOnly(listSpec)) {
      return false;
    }

    if (this.hasDetailFields(listSpec)) {
      const detailFieldsAllowsUpdate = listSpec.busListSpec.detailFields.some( (detailField: LMListDisplayField) => detailField.allowUpdate);
      if (detailFieldsAllowsUpdate) {
        return true;
      }
    }

    return listSpec.busListSpec.displayFields.some( (displayField: LMListDisplayField) => displayField.allowUpdate);
  }

  public implementsInlineCreate(listSpec: LMListSpec): boolean {

    if (listSpec.drillAroundMode) {
      return false;
    }

    if (this.lmContextService.isArchiveMode()) {
      return false;
    }

    if (listSpec.busListSpec.userFolderArchiveReport !== undefined ||listSpec.busListSpec.userFolderAsOfDate !== undefined) {
      return false;
    }

    if (this.hasDetailSections(listSpec)) {

      // ------------------------------------------------
      // does a detail section implement inline create
      // ------------------------------------------------

      const detailSectionImplementsInlineCreate = listSpec.busListSpec.detailSections.find(
        (detailSection: LMBusDetailSection) => {
          switch (detailSection.type) {
            /** a detail section that is a detail section?!?! */
            case 'detailsection':
            case 'cardview':
            case 'instancecountchart':
            case 'link':
            case 'standardform':
            case 'dashboard':
              return false; // none of these detail sections are editable.

            case 'list':
              return this.implementsInlineCreate(detailSection.listSpec);
          }
          return false;
        });

      if (detailSectionImplementsInlineCreate) {
        return true;
      }
    }

    if (this.isCardViewOnly(listSpec)) {
      return false;
    }

    return !!listSpec.busListSpec.implementsInlineCreate;
  }

  public isDetailSection(listSpec: LMListSpec): boolean {
    return (listSpec && listSpec.listType === 'detailsectionlist');
  }

  public isCardListDetailSection(listSpec: LMListSpec): boolean {
    return (listSpec && listSpec.listType === 'cardlistdetailsection');
  }

  public isDashBoardContent(listSpec: LMListSpec): boolean {
    return (listSpec && listSpec.listType === 'dashboard');
  }

  public isMultiPanePanelContent(listSpec: LMListSpec): boolean {
    return (listSpec && listSpec.listType === 'multipane');
  }

  public isMultiListPanelContent(listSpec: LMListSpec): boolean {
    return (listSpec && listSpec.listType === 'multilist');
  }

  public isFormListPanelContent(listSpec: LMListSpec): boolean {
    return (listSpec && listSpec.listType === 'formlist');
  }

  public isModalDialogContent(listSpec: LMListSpec): boolean {
    return (this.isSelectList(listSpec) || this.isHelperList(listSpec));
  }

  public isSelectList(listSpec: LMListSpec): boolean {
    return (listSpec && listSpec.listType === 'select');
  }

  public isHelperList(listSpec: LMListSpec): boolean {
    return (listSpec && listSpec.listType === 'helper');
  }

  public isAggChart(listSpec: LMListSpec, listStateInfo: LMListState): boolean {
    return (listSpec && listStateInfo.displayAs === 'chart' &&
        (listStateInfo.chartName && listSpec.busListSpec?.charts[listStateInfo.chartName]?.chartType === 'aggregation'));
  }

  public isChartValid(listSpec: LMListSpec, chartName: string, listStateInfo: LMListState): boolean {

    if (listSpec.busListSpec?.charts[chartName]?.visibility) {
      const visibility: LMCondition[] = [];
      visibility[0] = listSpec.busListSpec?.charts[chartName]?.visibility;
      const dataView: LMDataView = listStateInfo.chartVisibilityFields;
      return this.lmConditionUtilService.isValid(visibility, dataView);
    } else {
      return true;
    }

    return false;
  }

  public hasVisibleCharts(listSpec: LMListSpec, listStateInfo: LMListState): boolean {

    for (const chartName in listSpec?.busListSpec?.charts) {
      const isValid = this.isChartValid(listSpec, chartName, listStateInfo);
      if (isValid) {
        return true;
      }
    }

    return false;
  }

  public getFirstVisibleChart(listSpec: LMListSpec, listStateInfo: LMListState): LMChartSpec {

    if (this.isDefaultChartValid(listSpec, listStateInfo)) {
      return listSpec?.busListSpec?.charts[listSpec.busListSpec.defaultChartName];
    }

    for (const chartName in listSpec?.busListSpec?.charts) {
      const isValid = this.isChartValid(listSpec, chartName, listStateInfo);
      if (isValid) {
        return listSpec?.busListSpec?.charts[chartName];
      }
    }

    return undefined;
  }

  public isDefaultChartValid(listSpec: LMListSpec, listStateInfo: LMListState): boolean {

    if (listSpec.busListSpec.defaultChartName) {
      return this.isChartValid(listSpec, listSpec.busListSpec.defaultChartName, listStateInfo);
    }

    return false;
  }

  public hasSearchFields(listSpec: LMListSpec): boolean {
    // don't allow search forms mingle widgets
    if (listSpec.showAsMingleWidget) {
      return false;
    }

    // don't allow search forms in dashboard panels
    if (this.isDashBoardContent(listSpec)) {
      return false;
    }

    return !!listSpec?.busListSpec?.searchFields;
  }

  public hasAlphaDefaultFilter(listSpec: LMListSpec): boolean {
    return !!listSpec?.busListSpec?.alphaDefaultFilter;
  }

  public isListViewWithSorts(listSpec: LMListSpec): boolean {
    if (this.isCardViewOnly(listSpec)) {
      if (listSpec.busListSpec.displayFields[0].cardViewSortOrders) {
        return true;
      }
    }
    return false;
  }

  public cardViewSortOrders(listSpec: LMListSpec): Array<LMCardSortOrder> {
    if (this.isListViewWithSorts(listSpec)) {
      return listSpec.busListSpec.displayFields[0].cardViewSortOrders;
    }
    return [];
  }

  public isGridWithCardViewSorts(listSpec: LMListSpec): boolean {
    const cardViewDisplayField: LMListDisplayField = listSpec?.busListSpec?.displayFields.find(displayField => displayField.type.toUpperCase() === AppConstants.CARDVIEW);
    return (cardViewDisplayField && cardViewDisplayField?.cardViewSortOrders?.length > 0);
  }

  public gridCardViewSortOrders(listSpec: LMListSpec): Array<LMCardSortOrder> {
    if (this.isGridWithCardViewSorts(listSpec)) {
      return listSpec.busListSpec.displayFields[0].cardViewSortOrders;
    }
    return [];
  }

  public hasSortCalledOut(listSpec: LMListSpec): boolean {
    if (!listSpec?.busListSpec?.toolbar) {
      return false;
    }

    const sortAction = listSpec.busListSpec.toolbar.find(actionRef => actionRef.id === AppConstants.STANDARD_TOOLBAR_SORT);
    return sortAction !== undefined;
  }

  public isRequiredSearchParameter(busListSpec: LMBusListSpec, fieldId: string) {
    if (busListSpec.requiredSearchParameters && fieldId) {
      const requiredSearchField = busListSpec.requiredSearchParameters.find(rsp => rsp.fieldId === fieldId);
      return requiredSearchField ? true : false;
    } else {
      return false;
    }
  }

  public hasUserListViewForList(listSpec: LMListSpec) {
    const userListViewSupported = (!this.isDashBoardContent(listSpec) && !this.isFilterList(listSpec));
    return userListViewSupported;
  }
}
